import { useEffect, useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { toast } from "sonner"
import { Copy, Check, Crown, Loader2, LogOut, Mail, MessageSquare, UserMinus, Users, ShieldCheck, ShieldOff } from "lucide-react"

interface Member {
  id: number
  name: string
  is_admin: boolean
  is_current_user?: boolean
}

interface Params {
  id: number
  name: string
  current_user_name: string
  code: string
  is_admin: boolean
  members: Member[]
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

async function postForm(url: string, data: Record<string, string>, method: "POST" | "PUT" = "POST") {
  const body = new URLSearchParams(data)
  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: body.toString(),
    credentials: "same-origin",
  })
  if (!res.ok) throw new Error(String(res.status))
}

const demo = (id: number, name: string): Params => ({
  id,
  name,
  current_user_name: "Charlotte",
  code: "WMZ-4F7K2",
  is_admin: true,
  members: [
    { id: 11, name: "Charlotte", is_admin: true },
    { id: 12, name: "Hubert", is_admin: false },
    { id: 13, name: "Cédric", is_admin: false },
  ],
})

const mailTemplate = (p: Params) =>
  `Bonjour,\n\n${p.current_user_name} vous a invité(e) à rejoindre l’album : ${p.name}.\n\nPour y accéder :\nConnectez-vous sur wememberz.com et une fois connecté(e), entrez le code suivant pour rejoindre l’album : ${p.code}.`

const messageTemplate = (p: Params) =>
  `Salut, je t’invite à me rejoindre pour réaliser le livre ${p.name}.\nConnecte-toi sur wememberz.com et rentre le code « ${p.code} » dans l’onglet « Rejoindre un album invité ».\nÀ très vite !`

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null)
  const copy = (key: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key)
      toast.success("Copié !")
      setTimeout(() => setCopied(null), 1500)
    })
  }
  return { copied, copy }
}

export default function ShareAlbumDialog({
  projectId,
  projectName,
  open,
  onOpenChange,
}: {
  projectId: number
  projectName: string
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const [params, setParams] = useState<Params | null>(null)
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [email, setEmail] = useState("")
  const [mailMsg, setMailMsg] = useState("")
  const [shareMsg, setShareMsg] = useState("")
  const [sending, setSending] = useState(false)
  const { copied, copy } = useCopy()

  useEffect(() => {
    if (!open) return
    setLoading(true)
    fetch(`/social_networks/${projectId}/load_parameters.json`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status))
        return r.json()
      })
      .then((d: Params) => d)
      .catch(() => demo(projectId, projectName))
      .then((p) => {
        const members = Array.isArray(p.members) ? p.members : Object.values(p.members ?? {})
        const full = { ...p, members: members as Member[] }
        setParams(full)
        setMailMsg(mailTemplate(full))
        setShareMsg(messageTemplate(full))
      })
      .finally(() => setLoading(false))
  }, [open, projectId, projectName])

  const isMe = (m: Member) => m.is_current_user ?? m.name === params?.current_user_name

  const changeRole = async (m: Member) => {
    setBusyId(m.id)
    const role = m.is_admin ? "member" : "admin"
    try {
      await postForm("/social_networks/change_role", { social_network_user_id: String(m.id), role })
      setParams((p) => p && { ...p, members: p.members.map((x) => (x.id === m.id ? { ...x, is_admin: !m.is_admin } : x)) })
      toast.success(role === "admin" ? `${m.name} est maintenant admin` : `${m.name} est maintenant membre`)
    } catch {
      toast.error("Impossible de changer le rôle")
    } finally {
      setBusyId(null)
    }
  }

  const removeMember = async (m: Member) => {
    const me = isMe(m)
    if (!window.confirm(me ? "Quitter ce projet ?" : `Retirer ${m.name} du projet ?`)) return
    setBusyId(m.id)
    try {
      await postForm("/social_networks/leave_member", { social_network_user_id: String(m.id) })
      if (me) {
        window.location.href = "/social_networks"
        return
      }
      setParams((p) => p && { ...p, members: p.members.filter((x) => x.id !== m.id) })
      toast.success(`${m.name} a été retiré(e) du projet`)
    } catch {
      toast.error("Action impossible")
    } finally {
      setBusyId(null)
    }
  }

  const sendInvite = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast.error("Adresse e-mail invalide")
      return
    }
    setSending(true)
    try {
      await postForm(`/social_networks/${projectId}/send_invite`, { from: email.trim(), message: mailMsg }, "PUT")
      toast.success(`Invitation envoyée à ${email.trim()}`)
      setEmail("")
    } catch {
      toast.error("L'invitation n'a pas pu être envoyée")
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-display">Partager l'album</DialogTitle>
          <DialogDescription>{params?.name ?? projectName}</DialogDescription>
        </DialogHeader>

        {loading || !params ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Code */}
            <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-accent/10 to-secondary/10 p-5 text-center">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Code d'invitation</p>
              <div className="flex items-center justify-center gap-3">
                <span className="font-mono text-2xl md:text-3xl font-bold tracking-widest text-foreground">{params.code}</span>
                <Button size="icon" variant="outline" onClick={() => copy("code", params.code)} title="Copier le code">
                  {copied === "code" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-3">
                Partagez ce code avec vos proches : ils pourront rejoindre l'album, ajouter leurs photos et créer le livre avec vous.
              </p>
            </div>

            <Tabs defaultValue="members">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="members"><Users className="h-4 w-4 mr-1.5" />Membres</TabsTrigger>
                <TabsTrigger value="mail"><Mail className="h-4 w-4 mr-1.5" />E-mail</TabsTrigger>
                <TabsTrigger value="message"><MessageSquare className="h-4 w-4 mr-1.5" />Message</TabsTrigger>
              </TabsList>

              <TabsContent value="members" className="mt-4">
                <ul className="divide-y divide-border rounded-xl border border-border">
                  {params.members.map((m) => {
                    const me = isMe(m)
                    return (
                      <li key={m.id} className="flex items-center gap-3 px-4 py-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                          {m.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">
                            {m.name} {me && <span className="text-muted-foreground font-normal">(vous)</span>}
                          </p>
                          {m.is_admin ? (
                            <Badge variant="secondary" className="mt-0.5"><Crown className="h-3 w-3 mr-1 text-primary" />Admin</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">Membre</span>
                          )}
                        </div>
                        {busyId === m.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : (
                          <div className="flex items-center gap-1">
                            {params.is_admin && !me && (
                              <>
                                <Button size="icon" variant="ghost" onClick={() => changeRole(m)} title={m.is_admin ? "Passer en membre" : "Passer en admin"}>
                                  {m.is_admin ? <ShieldOff className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => removeMember(m)} title="Retirer du projet" className="hover:text-destructive">
                                  <UserMinus className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {!params.is_admin && me && (
                              <Button size="sm" variant="ghost" onClick={() => removeMember(m)} className="hover:text-destructive">
                                <LogOut className="h-4 w-4 mr-1" />Quitter
                              </Button>
                            )}
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </TabsContent>

              <TabsContent value="mail" className="mt-4 space-y-3">
                <Input type="email" placeholder="adresse@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <Textarea rows={8} value={mailMsg} onChange={(e) => setMailMsg(e.target.value)} />
                <Button variant="rainbow" className="w-full" onClick={sendInvite} disabled={sending}>
                  {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  Envoyer l'invitation
                </Button>
              </TabsContent>

              <TabsContent value="message" className="mt-4 space-y-3">
                <p className="text-sm text-muted-foreground">Copiez ce message et envoyez-le par SMS, WhatsApp ou messagerie.</p>
                <Textarea rows={6} value={shareMsg} onChange={(e) => setShareMsg(e.target.value)} />
                <Button variant="rainbow" className="w-full" onClick={() => copy("msg", shareMsg)}>
                  {copied === "msg" ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  Copier le message
                </Button>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
